import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
// Locales
import localeAr from '@angular/common/locales/ar';
import {
  APP_INITIALIZER,
  isDevMode,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import {
  NgxsStoragePluginModule,
  SESSION_STORAGE_ENGINE,
} from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { ApiConfig } from '@osos/shared/api';
import {
  AuthModule,
  LogLevel,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader,
} from 'angular-auth-oidc-client';
import { MessageService } from 'primeng/api';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AppConfiguration, ConfigService, UserUiSettings } from './config';
import { CORE_FACTORIES } from './factories';
import {
  AccessPermissionGuard,
  AuthGuard,
  PublicAccessPermissionGuard,
} from './guards';
import {
  AuthInterceptor,
  DelegationInterceptor,
  HttpQueueInterceptor,
  HttpResultInterceptor,
  PublicInterceptor,
  TourInterceptor,
} from './interceptors';
import { HeadlessAuthInterceptor } from './interceptors/auth-interceptor/headless-auth.interceptor';
import { GeneralErrorInterceptorInterceptor } from './interceptors/general-error-interceptor/general-error-interceptor.interceptor';
import { CORE_PARSERS } from './parsers';
import { LocaleProvider } from './providers';
import {
  BreadcrumbService,
  CollectionDataService,
  DataQueryService,
  DynamicComponentFactoryService,
  EntityDataService,
  EntryDataService,
  ExportDataService,
  ModuleDataService,
  NotificationDataService,
  ParserFactory,
  provideAuthService,
  ReportDataService,
  TypesUtilsService,
  ViewDataService,
  WorkflowDataService,
} from './services';
import {
  AppStateActions,
  CORE_STATES,
  DelegationActions,
  DisplayAvailableLanguages,
  EntityUIState,
  InitAuth,
  LanguageState,
  UserPreferencesActions,
  UserPreferencesState,
} from './state';
import { headLessQueryParams } from './utils';
// Register locales data:
registerLocaleData(localeAr, 'ar');

export function configureApp(
  configService: ConfigService,
  store: Store,
): () => Observable<any> {
  // Set headlessModeConfig in app setting

  return () =>
    forkJoin({
      appConfig: configService.loadAppConfiguration(),
      orgConfig: configService.loadOrgConfiguration(),
    }).pipe(
      tap(({ appConfig }) => {
        if (appConfig.IS_HEADLESS_BROWSER) {
          const { language, timeZoneOffset, userId } = headLessQueryParams();
          store.dispatch(
            new AppStateActions.SetHeadlessModeConfig(true, {
              language,
              timeZoneOffset: timeZoneOffset as unknown as number,
              userId,
            }),
          );
        }
      }),
      switchMap(() =>
        store.dispatch(new DelegationActions.LoadActiveDelegationDetails()),
      ),
      switchMap(() => store.dispatch(new InitAuth())),
      switchMap(() => store.dispatch(new DisplayAvailableLanguages())),
      switchMap(() =>
        store.dispatch(new UserPreferencesActions.FetchUserStateData()),
      ),
      tap(() =>
        store.dispatch(new UserPreferencesActions.DisplayAvailableThemes()),
      ),
      switchMap(() => store.selectOnce(UserPreferencesState.selectUserState)),
      tap((res) => configService.loadUserSettings(res)),
      switchMap(() => configService.loadVersion()),

      switchMap((version) => {
        configService.initOrgPreferences(version);
        return store.dispatch(
          new AppStateActions.SetDefaultLanguageConfig(
            configService.userUiConfig.primaryLanguage ??
              configService.configuration.DEFAULT_LANGUAGE,
          ),
        );
      }),

      catchError((err: unknown) => {
        return throwError(() => err);
      }),
    );
}

export const httpLoaderFactory = (configService: ConfigService) => {
  const config$ = configService.loadAppConfiguration().pipe(
    map((config: AppConfiguration) => {
      return {
        authority: `${config.AUTHORITY}/.well-known/openid-configuration`,
        redirectUrl: `${config.APP_URL}/auth-callback`,
        postLogoutRedirectUri: config.APP_URL,
        clientId: config.CLIENT_ID,
        maxIdTokenIatOffsetAllowedInSeconds:
          config.MAX_ID_TOKEN_IAT_OFFSET_ALLOWED_IN_SECONDS ?? undefined,
        scope: config.SCOPE,
        responseType: 'code',
        startCheckSession: true,
        autoUserInfo: true,
        useRefreshToken: false,
        silentRenew: true,
        silentRenewUrl: `${config.APP_URL}/silent-renew.html`,
        renewTimeBeforeTokenExpiresInSeconds: 300,
        tokenRefreshInSeconds: 15,
        triggerRefreshWhenIdTokenExpired: false,
        logLevel: isDevMode() ? LogLevel.Debug : LogLevel.None,
        authWellknownEndpoints: {
          authorizationEndpoint: `${config.AUTHORITY}/auth`,
          issuer: `${config.AUTHORITY}`,
          jwksUri: `${config.AUTHORITY}/jwks`,
          endSessionEndpoint: `${config.AUTHORITY}/session/end`,
          tokenEndpoint: `${config.AUTHORITY}/token`,
          userInfoEndpoint: `${config.AUTHORITY}/me`,
        },
      } satisfies OpenIdConfiguration;
    }),
  );
  return new StsConfigHttpLoader(config$);
};

export function appConfig(configService: ConfigService): AppConfiguration {
  return configService.configuration;
}
export function userUiSettings(configService: ConfigService): UserUiSettings {
  return configService.userUiConfig;
}
export function headlessBrowser(configService: ConfigService) {
  return configService?.configuration?.IS_HEADLESS_BROWSER
    ? new HeadlessAuthInterceptor()
    : new AuthInterceptor();
}
@NgModule({
  imports: [
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: httpLoaderFactory,
        deps: [ConfigService],
      },
    }),
    NgxsModule.forRoot(CORE_STATES, { developmentMode: isDevMode() }),
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: !isDevMode() }),
    NgxsLoggerPluginModule.forRoot({ disabled: !isDevMode() }),
    NgxsStoragePluginModule.forRoot({
      keys: [
        LanguageState,
        // UI States
        UserPreferencesState,
        {
          key: EntityUIState,
          engine: SESSION_STORAGE_ENGINE,
        },
      ],
    }),
  ],
  exports: [AuthModule],
  providers: [
    MessageService,
    ConfigService,
    ApiConfig,
    {
      provide: APP_INITIALIZER,
      useFactory: configureApp,
      deps: [ConfigService, Store],
      multi: true,
    },

    provideAuthService(),
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: headlessBrowser,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DelegationInterceptor,
      multi: true,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpResultInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PublicInterceptor,
      multi: true,
    },
    {
      // Note: this should be last interceptor.
      provide: HTTP_INTERCEPTORS,
      useClass: TourInterceptor,
      multi: true,
    },
    {
      provide: AppConfiguration,
      useFactory: appConfig,
      deps: [ConfigService],
    },
    {
      provide: UserUiSettings,
      useFactory: userUiSettings,
      deps: [ConfigService],
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: GeneralErrorInterceptorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpQueueInterceptor,
      deps: [ConfigService],
      multi: true,
    },

    LocaleProvider,
    CORE_FACTORIES,
    CORE_PARSERS,

    // Guards
    AuthGuard,
    AccessPermissionGuard,
    PublicAccessPermissionGuard,

    // Data Services
    EntityDataService,
    CollectionDataService,
    ModuleDataService,
    ViewDataService,
    ReportDataService,
    EntryDataService,
    NotificationDataService,
    DataQueryService,
    ExportDataService,
    WorkflowDataService,
    // Utils Services
    ParserFactory,
    DynamicComponentFactoryService,
    TypesUtilsService,
    BreadcrumbService,
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error(
        'CoreModule is already loaded. Import in your base AppModule only.',
      );
    }
  }
}
